/**
 * Globale Bestenliste: alle Partien, sortiert nach Unternehmenswert.
 *
 * Jede Zeile ist eine Partie, nicht ein Nutzer – bei gemeinsam gespielten
 * Partien zählt die Firma als Ganzes, nicht ein einzelnes Mitglied. Die
 * eigenen Partien stehen zusätzlich gesondert, mit Rang auch dann, wenn sie
 * außerhalb der sichtbaren Liste liegen.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, DataTable, PageHeader, Spinner } from '../components/ui';
import { BrandLogo } from '../components/layout/BrandLogo';
import { api, type LeaderboardEntry } from '../lib/api';
import { money, num } from '../lib/format';

export function Leaderboard() {
  const [top, setTop] = useState<LeaderboardEntry[] | null>(null);
  const [mine, setMine] = useState<LeaderboardEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [top, mine] = await Promise.all([api.leaderboard(50), api.myRank()]);
        setTop(top);
        setMine(mine);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, []);

  const topGameIds = new Set((top ?? []).map((e) => e.gameId));
  const ownOutside = (mine ?? []).filter((e) => !topGameIds.has(e.gameId));

  return (
    <div className="min-h-screen bg-panel-100 dark:bg-panel-950">
      <header className="flex items-center justify-between border-b divider px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo className="h-8" />
        </Link>
        <Link to="/" className="text-sm text-panel-500 hover:text-panel-800 dark:text-panel-400 dark:hover:text-panel-100">
          Zurück zu den Partien
        </Link>
      </header>

      <div className="mx-auto max-w-4xl space-y-4 p-6">
        <PageHeader title="Bestenliste" description="Alle Partien nach Unternehmenswert – Anlagen, Adressen, Kundenstamm, Kasse minus Schulden." />

        {error && <Card>{error}</Card>}
        {!top && !error && (
          <Card>
            <div className="flex items-center gap-2 text-sm text-panel-500 dark:text-panel-400">
              <Spinner className="h-4 w-4" /> Lädt…
            </div>
          </Card>
        )}

        {top && (
          <Card padded={false}>
            <DataTable
              columns={[
                { key: 'rank', header: '#', render: (r) => r.rank, width: '3rem' },
                { key: 'companyName', header: 'Firma', render: (r) => r.companyName },
                { key: 'name', header: 'Partie', render: (r) => r.name, hideOnMobile: true },
                { key: 'owner', header: 'Eigentümer', render: (r) => r.ownerDisplayName, hideOnMobile: true },
                { key: 'companyValue', header: 'Unternehmenswert', render: (r) => money(r.companyValue, true), align: 'right' },
                { key: 'customers', header: 'Kunden', render: (r) => num(r.customers), align: 'right', hideOnMobile: true },
              ]}
              rows={top}
              rowKey={(r) => r.gameId}
              empty="Noch keine gespeicherten Partien."
            />
          </Card>
        )}

        {ownOutside.length > 0 && (
          <Card title="Eigene Partien außerhalb der Liste">
            <DataTable
              columns={[
                { key: 'rank', header: '#', render: (r) => r.rank, width: '3rem' },
                { key: 'companyName', header: 'Firma', render: (r) => r.companyName },
                { key: 'companyValue', header: 'Unternehmenswert', render: (r) => money(r.companyValue, true), align: 'right' },
              ]}
              rows={ownOutside}
              rowKey={(r) => r.gameId}
            />
          </Card>
        )}
      </div>
    </div>
  );
}
